const PATH_TO_FEATURE_FLAGS = 'photoheight/feature_flags';

/** Class to manage feature flags available to a user associated with a company, on a specific server */
class FeatureFlagsList {
  #publicFlagsAreLoaded = false;
  #serverFlagsAreLoaded = false;
  #companyFlagsAreLoaded = false;
  #userFlagsAreLoaded = false;
  #allFlagsAreLoaded = false;

  /**
   *
   * @param {object} db - Firebase database namespace
   * @param {object} context - Object containing the user context
   * @param {object} options - Options object
   */
  constructor(db, context, options) {
    this.flagsRef = db.ref(PATH_TO_FEATURE_FLAGS);
    this.context = context;

    // Object to store the flags
    this.flags = {};
    // Counter to keep track of the number of listeners added to the flags (used as unique IDs)
    this.flagListenerCounter = 0;

    // Initialize listeners to be called when the flags are loaded
    this.allFlagsLoadedListeners = {};
    this.allFlagsLoadedListenerCounter = 0;
    this.publicFlagsLoadedListeners = {};
    this.publicFlagsLoadedListenerCounter = 0;

    // Currently unused
    this.options = options;

    this.#updateDatabaseListeners();
  }

  get allFlagsAreLoaded() {
    return this.#allFlagsAreLoaded;
  }

  get publicFlagsAreLoaded() {
    return this.#publicFlagsAreLoaded;
  }

  /** @param {boolean} loaded */
  set publicFlagsAreLoaded(loaded) {
    // Don't update the public flags loaded status if it hasn't changed
    if (this.#publicFlagsAreLoaded == loaded) return;
    this.#publicFlagsAreLoaded = loaded;

    // Reset the all flags loaded status
    this.allFlagsAreLoaded =
      this.#publicFlagsAreLoaded && this.#serverFlagsAreLoaded && this.#companyFlagsAreLoaded && this.#userFlagsAreLoaded;

    // Call the public flags loaded listeners
    Object.values(this.publicFlagsLoadedListeners).forEach((callback) => callback(loaded));
  }

  /** @param {boolean} loaded */
  set serverFlagsAreLoaded(loaded) {
    // Don't update the server flags loaded status if it hasn't changed
    if (this.#serverFlagsAreLoaded == loaded) return;
    this.#serverFlagsAreLoaded = loaded;

    // Reset the all flags loaded status
    this.allFlagsAreLoaded =
      this.#publicFlagsAreLoaded && this.#serverFlagsAreLoaded && this.#companyFlagsAreLoaded && this.#userFlagsAreLoaded;
  }

  /** @param {boolean} loaded */
  set companyFlagsAreLoaded(loaded) {
    // Don't update the company flags loaded status if it hasn't changed
    if (this.#companyFlagsAreLoaded == loaded) return;
    this.#companyFlagsAreLoaded = loaded;

    // Reset the all flags loaded status
    this.allFlagsAreLoaded =
      this.#publicFlagsAreLoaded && this.#serverFlagsAreLoaded && this.#companyFlagsAreLoaded && this.#userFlagsAreLoaded;
  }

  /** @param {boolean} loaded */
  set userFlagsAreLoaded(loaded) {
    // Don't update the user flags loaded status if it hasn't changed
    if (this.#userFlagsAreLoaded == loaded) return;
    this.#userFlagsAreLoaded = loaded;

    // Reset the all flags loaded status
    this.allFlagsAreLoaded =
      this.#publicFlagsAreLoaded && this.#serverFlagsAreLoaded && this.#companyFlagsAreLoaded && this.#userFlagsAreLoaded;
  }

  /** @param {boolean} loaded */
  set allFlagsAreLoaded(loaded) {
    // Don't update the all flags loaded status if it hasn't changed
    if (this.#allFlagsAreLoaded == loaded) return;
    this.#allFlagsAreLoaded = loaded;

    // Call the all flags loaded listeners
    Object.values(this.allFlagsLoadedListeners).forEach((callback) => callback(loaded));
  }

  /**
   * Update the user context (also triggers an update of the flag listeners)
   * @param {object} updatedContext - Object containing the updated user context
   * @param {string} updatedContext.companyId - ID of the company
   * @param {string} updatedContext.userId - ID of the user
   */
  updateContext(updatedContext) {
    this.context = { ...this.context, ...updatedContext };

    // Turn off the database listeners (so they don't inadvertently reset the "loaded" statuses)
    if (this.companyFlagsListener && this.companyFlagsRef) this.companyFlagsRef.off('value', this.companyFlagsListener);
    if (this.userFlagsListener && this.userFlagsRef) this.userFlagsRef.off('value', this.userFlagsListener);

    // Unset the "loaded" status for the flags
    this.companyFlagsAreLoaded = false;
    this.userFlagsAreLoaded = false;

    // Update the database listeners
    this.#updateDatabaseListeners();
  }

  getContext() {
    return this.context;
  }

  /**
   * Get a flag object
   * @param {string} flagName - Name of the flag
   * @returns {FeatureFlag} - Feature flag object
   */
  getFlag(flagName) {
    const flag = this.flags[flagName];

    // If the flag doesn't exist, create and return a blank flag
    if (!flag) {
      const blankFlag = new FeatureFlag(flagName);
      this.flags[flagName] = blankFlag;
      return blankFlag;
    }

    // If the flag exists, return the flag
    return flag;
  }

  /**
   * Get the enabled status of a flag
   * @param {string} flagName - Name of the flag
   * @returns {boolean} - True if the flag is enabled, false if not
   */
  isFlagEnabled(flagName) {
    const flag = this.getFlag(flagName);
    return flag.toObject().enabled;
  }

  /**
   * Add a listener to a flag
   * @param {string} flagName - Name of the flag
   * @param {function} callback - Callback function to be called when the flag enabled status changes
   * @returns {number} - ID of the listener (used to remove the listener)
   */
  addFlagListener(flagName, callback) {
    const flag = this.getFlag(flagName);
    const listenerId = this.flagListenerCounter++;
    flag.addListener(listenerId, callback);

    return listenerId;
  }

  /**
   * Remove a listener from a flag
   * @param {string} flagName - Name of the flag
   * @param {number} listenerId - ID of the listener to remove
   */
  removeFlagListener(flagName, listenerId) {
    const flag = this.getFlag(flagName);
    flag.removeListener(listenerId);
  }

  /**
   * Add a listener to be called when all flags are loaded from the database
   * @param {function} callback - Callback function to be called when all flags are loaded from the database
   * @returns {number} ID of the listener (used to remove the listener)
   */
  addAllFlagsLoadedListener(callback) {
    const listenerId = this.allFlagsLoadedListenerCounter++;
    this.allFlagsLoadedListeners[listenerId] = callback;

    // Call the callback immediately if all flags are already loaded
    callback(this.allFlagsAreLoaded);

    return listenerId;
  }

  /**
   * Remove a listener from the all flags loaded listener list
   * @param {number} listenerId - ID of the listener to remove
   */
  removeAllFlagsLoadedListener(listenerId) {
    delete this.allFlagsLoadedListeners[listenerId];
  }

  /**
   * Add a listener to be called when public flags are loaded from the database
   * @param {function} callback - Callback function to be called when public flags are loaded from the database
   * @returns {number} ID of the listener (used to remove the listener)
   */
  addPublicFlagsLoadedListener(callback) {
    const listenerId = this.publicFlagsLoadedListenerCounter++;
    this.publicFlagsLoadedListeners[listenerId] = callback;

    // Call the callback immediately
    callback(this.publicFlagsAreLoaded);

    return listenerId;
  }

  /**
   * Remove a listener from the public flags loaded listener list
   * @param {number} listenerId - ID of the listener to remove
   */
  removePublicFlagsLoadedListener(listenerId) {
    delete this.publicFlagsLoadedListeners[listenerId];
  }

  /** Update the listeners for the public, server, company, and user flags in the database */
  #updateDatabaseListeners() {
    if (this.publicFlagsListener && this.publicFlagsRef) this.publicFlagsRef.off('value', this.publicFlagsListener);
    this.publicFlagsRef = this.flagsRef.child('public');
    this.publicFlagsListener = this.publicFlagsRef.on('value', (snapshot) => {
      const publicFlags = snapshot.exists() ? snapshot.val() : {};
      this.#updateFlags(publicFlags, 'public');
      this.publicFlagsAreLoaded = true;
    });

    if (this.serverFlagsListener && this.serverFlagsRef) this.serverFlagsRef.off('value', this.serverFlagsListener);
    this.serverFlagsRef = this.flagsRef.child('server');
    this.serverFlagsListener = this.serverFlagsRef.on('value', (snapshot) => {
      const serverFlags = snapshot.exists() ? snapshot.val() : {};
      this.#updateFlags(serverFlags, 'server');
      this.serverFlagsAreLoaded = true;
    });

    if (this.companyFlagsListener && this.companyFlagsRef) this.companyFlagsRef.off('value', this.companyFlagsListener);
    if (this.context.companyId != null) {
      this.companyFlagsRef = this.flagsRef.child(`companies/${this.context.companyId}`);
      this.companyFlagsListener = this.companyFlagsRef.on('value', (snapshot) => {
        const companyFlags = snapshot.exists() ? snapshot.val() : {};
        this.#updateFlags(companyFlags, 'company');
        this.companyFlagsAreLoaded = true;
      });
    }

    if (this.userFlagsListener && this.userFlagsRef) this.userFlagsRef.off('value', this.userFlagsListener);
    if (this.context.userId != null) {
      this.userFlagsRef = this.flagsRef.child(`users/${this.context.userId}`);
      this.userFlagsListener = this.userFlagsRef.on('value', (snapshot) => {
        const userFlags = snapshot.exists() ? snapshot.val() : {};
        this.#updateFlags(userFlags, 'user');
        this.userFlagsAreLoaded = true;
      });
    }
  }

  /** Update the flags with the data from Firebase Database on a given scope */
  #updateFlags(updatedFlagData, scope) {
    // Build a list of all the flag names (new and existing) so we can handle flags that are removed
    const existingFlagNames = Object.keys(this.flags ?? {});
    const updatedFlagNames = Object.keys(updatedFlagData ?? {});
    const allFlagNames = [...new Set([...existingFlagNames, ...updatedFlagNames])];

    // For each flag (new or existing), update the flag on the given scope
    allFlagNames.forEach((flagName) => {
      this.#updateFlag(flagName, scope, Boolean(updatedFlagData?.[flagName]));
    });
  }

  /** Update a specific flag on a given scope */
  #updateFlag(flagName, scope, enabled) {
    if (!this.flags[flagName]) this.flags[flagName] = new FeatureFlag(flagName);
    const flag = this.flags[flagName];

    switch (scope) {
      case 'public':
        flag.enabledForPublic = enabled;
        break;
      case 'server':
        flag.enabledForServer = enabled;
        break;
      case 'company':
        flag.enabledForCompany = enabled;
        break;
      case 'user':
        flag.enabledForUser = enabled;
        break;
      default:
        throw new Error(`Unknown scope: ${scope}`);
    }
  }
}

/** Simple class to represent a feature flag */
class FeatureFlag {
  #enabled = false;
  #enabledForPublic = false;
  #enabledForServer = false;
  #enabledForCompany = false;
  #enabledForUser = false;

  /** @param {string} flagName - Name of the feature flag (should match the name in Firebase) */
  constructor(flagName) {
    this.flagName = flagName;
    this.listeners = {};
  }

  /** Checks if the flag has any listeners */
  get #hasListeners() {
    return Object.keys(this.listeners).length > 0;
  }

  /** @param {boolean} enabled */
  set enabledForPublic(enabled) {
    this.#enabledForPublic = enabled;
    this.#updateEnabled();
  }
  /** @param {boolean} enabled */
  set enabledForServer(enabled) {
    this.#enabledForServer = enabled;
    this.#updateEnabled();
  }
  /** @param {boolean} enabled */
  set enabledForCompany(enabled) {
    this.#enabledForCompany = enabled;
    this.#updateEnabled();
  }
  /** @param {boolean} enabled */
  set enabledForUser(enabled) {
    this.#enabledForUser = enabled;
    this.#updateEnabled();
  }

  /**
   * Add a listener to the flag (and call the listener immediately)
   * @param {number} listenerId - Unique ID number for the listener
   * @param {function} callback - Callback function to be called when the flag is updated
   */
  addListener(listenerId, callback) {
    if (typeof callback !== 'function') throw new Error('Feature flag listener callback must be a function');
    this.listeners[listenerId] = callback;

    // Call the listener immediately
    const flagData = this.toObject();
    callback(flagData.enabled, flagData);
  }

  /**
   * Remove a listener from the flag
   * @param {number} listenerId - Unique ID number for the listener
   */
  removeListener(listenerId) {
    delete this.listeners[listenerId];
  }

  /**
   * Returns an object containing the flag data
   * @returns {object} - Object containing the flag data
   */
  toObject() {
    return {
      enabled: this.#enabled,
      enabledForPublic: this.#enabledForPublic,
      enabledForServer: this.#enabledForServer,
      enabledForCompany: this.#enabledForCompany,
      enabledForUser: this.#enabledForUser,
      flagName: this.flagName
    };
  }

  /** Updates the enabled status of the flag; calls listeners if the status has changed */
  #updateEnabled() {
    const wasEnabled = this.#enabled;
    this.#enabled = this.#enabledForPublic || this.#enabledForServer || this.#enabledForCompany || this.#enabledForUser;
    if (wasEnabled !== this.#enabled && this.#hasListeners) {
      const flagData = this.toObject();
      Object.values(this.listeners).forEach((callback) => callback(flagData.enabled, flagData));
    }
  }
}

/** Error thrown when the feature flags module has not been initialized */
class NotInitializedError extends Error {
  constructor() {
    super('Feature flags module has not been initialized');
    this.name = 'NotInitializedError';
  }
}

/** Error thrown when the feature flags module has already been initialized */
class AlreadyInitializedError extends Error {
  constructor() {
    super('Feature flags module has already been initialized');
    this.name = 'AlreadyInitializedError';
  }
}

/**
 * Instance of the feature flags list (exists in the shared module scope)
 * @type {FeatureFlagsList}
 */
let _instance;

/**
 * Initialize the feature flags module (should only be called once)
 * @param {object} db - Firebase database namespace
 * @param {object} context - Context object containing user and company information
 * @param {string|null} context.companyId - Company ID of the current user
 * @param {string|null} context.userId - User ID of the current user
 * @param {object} options - Options object
 */
export function init(db, context = { companyId: null, userId: null }, options = {}) {
  if (_instance) throw new AlreadyInitializedError();
  if (typeof db.ref !== 'function') throw new Error('Feature flag module requires a Firebase database instance');

  _instance = new FeatureFlagsList(db, context, options);
}

/**
 * Update context information for the feature flags list (ommited fields will not be updated, unless null is passed, which clears the context)
 * @param {object|null} updatedContext - Updated context object containing user and company information
 * @returns {object} - Updated context object
 */
export function updateContext(updatedContext) {
  if (!_instance) throw new NotInitializedError();

  return _instance.updateContext(updatedContext ?? { companyId: null, userId: null });
}

/**
 * Get the current flag list context
 * @returns {object} - Current context object
 */
export function getContext() {
  if (!_instance) throw new NotInitializedError();

  return _instance.getContext();
}

/**
 * Get a flag object by name
 * @param {string} flagName - Name of the flag to get
 * @returns {object} - Flag object with the following properties: enabled, enabledForPublic, enabledForServer, enabledForCompany, enabledForUser, flagName
 */
export function getCachedFlag(flagName) {
  if (!_instance) throw new NotInitializedError();

  return _instance.getFlag(flagName).toObject();
}

/**
 * Get the enabled/disabled status of a flag
 * @param {string} flagName - Name of the flag to check
 * @returns {boolean} - True if the flag is enabled, false otherwise
 */
export function isCachedFlagEnabled(flagName) {
  if (!_instance) throw new NotInitializedError();

  return _instance.isFlagEnabled(flagName);
}

/**
 * @deprecated
 * This method has been replaced by `isCachedFlagEnabled`; you may also want to use `isFlagEnabled` to check the status of a flag without using the local cache
 */
export function isEnabled(flagName) {
  return isCachedFlagEnabled(flagName);
}

/**
 * Listen for changes to the enabled/disabled status of a flag
 * @param {string} flagName - Name of the flag to listen for enabled/disabled changes
 * @param {function} callback - Callback function to call when the flag is enabled/disabled
 * @returns {number} - Listener ID number that can be used to remove the listener
 */
export function addFlagListener(flagName, callback) {
  if (!_instance) throw new NotInitializedError();

  return _instance.addFlagListener(flagName, callback);
}

/**
 * Remove a listener from a flag
 * @param {string} flagName - Name of the flag to remove the listener from
 * @param {number} listenerId - Listener ID number to remove (returned from listenFlag)
 */
export function removeFlagListener(flagName, listenerId) {
  if (!_instance) throw new NotInitializedError();

  _instance.removeFlagListener(flagName, listenerId);
}

/**
 * Register listener to see if all flags have been loaded from the database
 * @param {function} callback - Callback function to call when all flags are loaded from the database
 * @returns {number} Listener ID number that can be used to remove the listener
 */
export function addAllFlagsLoadedListener(callback) {
  if (!_instance) throw new NotInitializedError();

  return _instance.addAllFlagsLoadedListener(callback);
}

/**
 * Remove a listener from the all flags loaded listener list
 * @param {number} listenerId - ID of the listener to remove
 */
export function removeAllFlagsLoadedListener(listenerId) {
  if (!_instance) throw new NotInitializedError();

  _instance.removeAllFlagsLoadedListener(listenerId);
}

/**
 * Register listener to see if public flags have been loaded from the database
 * @param {function} callback - Callback function to call when a public flag is loaded from the database
 * @returns {number} Listener ID number that can be used to remove the listener
 */
export function addPublicFlagsLoadedListener(callback) {
  if (!_instance) throw new NotInitializedError();

  return _instance.addPublicFlagsLoadedListener(callback);
}

/**
 * Remove a listener from the public flags loaded listener list
 * @param {number} listenerId - ID of the listener to remove
 */
export function removePublicFlagsLoadedListener(listenerId) {
  if (!_instance) throw new NotInitializedError();

  _instance.removePublicFlagsLoadedListener(listenerId);
}

/**
 * Check if a feature flag is enabled without using local cache
 * @param {string} flagName - Name of the flag to check for
 * @param {import('./types/ClientOrAdminFirebase.js').ClientOrAdminDatabase} database - Firebase database namespace
 * @param {{ companyId?: string, userId?: string } | boolean} [authContext=false]
 * - Auth context data
 *   - If omitted or false, only public flags will be checked
 *   - If truthy, public and server flags will be checked (must be authenticated)
 *   - If an object containing `companyId` and/or `userId`, public, server, company, and user flags will be checked (must be authenticated)
 * @returns {Promise<boolean>} True if the flag is enabled, false otherwise
 */
export async function isFlagEnabled(flagName, database, authContext = false) {
  // Validate parameters
  if (typeof flagName !== 'string') throw new Error('Flag name must be a string');
  if (typeof database?.ref !== 'function') throw new Error('Database must be a Firebase database (compat) instance');

  // Build the list of paths to check
  const flagPathsToCheck = ['public'];
  if (authContext) flagPathsToCheck.push('server');
  if (typeof authContext === 'object') {
    if (authContext.companyId) flagPathsToCheck.push(`companies/${authContext.companyId}`);
    if (authContext.userId) flagPathsToCheck.push(`users/${authContext.userId}`);
  }

  // Check if the flag is enabled on any of the paths
  const flagsRef = database.ref(PATH_TO_FEATURE_FLAGS);
  const flagEnabledPromises = flagPathsToCheck.map(async (path) => (await flagsRef.child(path).child(flagName).once('value')).val());
  const flagEnabled = (await Promise.all(flagEnabledPromises)).includes(true);

  return flagEnabled;
}
